import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'

/**
 * Shared shell for every PQR Preview section modal (Identifiers, Materials,
 * Joint Prep, Welding Variables, Welding Parameters, Notes, Comments,
 * Signatures).
 *
 * Responsibilities:
 *   - Render a centered modal panel with a title, optional "Edit in WPS form"
 *     link in the header, scrollable body, and Apply / Cancel footer.
 *   - Apply commits the modal's working copy via `onApply` and closes.
 *   - Cancel discards. When `isModalDirty` is `true`, both Cancel and
 *     backdrop click / Esc require a confirmation prompt before closing.
 *   - Esc and backdrop click are treated like Cancel.
 *
 * The shell does NOT own the working copy or the staged snapshot — it only
 * proxies user intent (Apply vs Cancel). Bodies own their working copy and
 * decide what "dirty" means.
 */

export interface PqrSectionModalProps {
  open: boolean
  title: string
  /**
   * Optional URL pointing at the corresponding WPS editor for power edits
   * not covered by the modal. Rendered as a small "Edit in WPS form" link
   * in the modal header.
   */
  editInWpsHref?: string
  /**
   * The modal body owns the working copy and tells the shell whether the
   * user has touched it. When `true`, Cancel / Esc / backdrop trigger a
   * confirmation before discarding.
   */
  isModalDirty: boolean
  /**
   * Label for the primary action. Defaults to "Apply" (the wording the PRD
   * agreed for staging-only commits inside the modal).
   */
  applyLabel?: string
  /**
   * Optional reason to disable Apply, e.g. validation. When provided, the
   * Apply button is disabled and the string is shown as its tooltip.
   */
  applyDisabledReason?: string
  /** Optional `maxWidth` Tailwind class, defaults to `max-w-2xl`. */
  panelMaxWidthClassName?: string
  /** Optional `max-h-*` Tailwind class for the modal panel. Defaults to `max-h-[80vh]`. */
  panelMaxHeightClassName?: string
  /**
   * Optional `max-h-*` Tailwind class for the scrollable body slot.
   * Defaults to `max-h-[70vh]`. Override for content-heavy modals
   * (e.g. the all-versions list) that benefit from more vertical
   * room.
   */
  bodyMaxHeightClassName?: string
  /**
   * When `true`, suppresses both the standard Apply/Cancel footer and
   * the discard-confirm footer. Useful for read-only / informational
   * modals (the "All versions" listing) where the only close
   * affordance needed is the header X button + Esc + backdrop click.
   * Defaults to `false`.
   */
  hideFooter?: boolean
  /**
   * Optional confirmation text shown on Cancel-when-dirty. Defaults to a
   * generic prompt. Customise per modal when richer copy helps.
   */
  cancelConfirmMessage?: string
  onApply: () => void
  onCancel: () => void
  children: React.ReactNode
}

const DEFAULT_CONFIRM = 'Discard your unsaved changes in this section?'

export default function PqrSectionModal({
  open,
  title,
  editInWpsHref,
  isModalDirty,
  applyLabel = 'Apply',
  applyDisabledReason,
  panelMaxWidthClassName = 'max-w-2xl',
  panelMaxHeightClassName = 'max-h-[80vh]',
  bodyMaxHeightClassName = 'max-h-[70vh]',
  hideFooter = false,
  cancelConfirmMessage = DEFAULT_CONFIRM,
  onApply,
  onCancel,
  children
}: PqrSectionModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  // Inline cancel-confirm UX (round 3 of pqr-five-changes):
  // instead of using window.confirm() — which feels jarring inside a
  // dialog and varies in style per browser — we flip the footer into
  // a "you have unsaved changes" banner with explicit Keep editing /
  // Discard buttons. Same semantics, friendlier UI.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  useEffect(() => {
    if (!open) setConfirmingDiscard(false)
  }, [open])

  // Latch latest props inside the keydown listener so we don't reattach on
  // every render (and so the listener always sees the current dirty flag).
  const isDirtyRef = useRef(isModalDirty)
  isDirtyRef.current = isModalDirty
  const onCancelRef = useRef(onCancel)
  onCancelRef.current = onCancel
  const setConfirmingRef = useRef(setConfirmingDiscard)
  setConfirmingRef.current = setConfirmingDiscard

  useEffect(() => {
    if (!open) return
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      if (isDirtyRef.current) {
        // Surface the inline confirm UI instead of a native dialog.
        setConfirmingRef.current(true)
        return
      }
      onCancelRef.current()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open])

  if (!open) return null

  const requestCancel = () => {
    if (isModalDirty) {
      setConfirmingDiscard(true)
      return
    }
    onCancel()
  }

  const confirmDiscard = () => {
    setConfirmingDiscard(false)
    onCancel()
  }

  const keepEditing = () => setConfirmingDiscard(false)

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    requestCancel()
  }

  const applyDisabled = Boolean(applyDisabledReason)

  const content = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pqr-section-modal-title"
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        className={`relative w-full ${panelMaxWidthClassName} ${panelMaxHeightClassName ? `max-h-[${panelMaxHeightClassName}]` : ''} rounded-xl border border-gray-200 bg-white shadow-xl`}
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h2
              id="pqr-section-modal-title"
              className="text-lg font-semibold text-gray-900"
            >
              {title}
            </h2>
            {editInWpsHref ? (
              <a
                href={editInWpsHref}
                className="mt-0.5 inline-block text-xs font-medium text-brand-red-600 hover:underline"
              >
                Edit in WPS form
              </a>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            onClick={requestCancel}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className={`${bodyMaxHeightClassName} overflow-y-auto px-5 py-4`}>{children}</div>

        {hideFooter ? null : confirmingDiscard ? (
          <footer
            className="flex flex-col gap-2 border-t border-hero-red-30 bg-hero-red-10 px-5 py-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid="pqr-section-modal-discard-confirm"
            role="alert"
          >
            <span className="text-sm text-hero-red-90">{cancelConfirmMessage}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={keepEditing}
                data-testid="pqr-section-modal-keep-editing"
              >
                Keep editing
              </button>
              <button
                type="button"
                className="rounded-md bg-hero-red px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:bg-hero-red-60 focus:outline-none focus:ring-2 focus:ring-hero-red focus:ring-offset-2"
                onClick={confirmDiscard}
                data-testid="pqr-section-modal-discard"
              >
                Discard changes
              </button>
            </div>
          </footer>
        ) : (
          <footer className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-3">
            <button
              type="button"
              className="btn-secondary"
              onClick={requestCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onApply}
              disabled={applyDisabled}
              title={applyDisabledReason}
            >
              {applyLabel}
            </button>
          </footer>
        )}
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
